import { app as microsoftTeams, authentication as teamsAuthentication } from '@microsoft/teams-js';
import AuthenticationContext from 'adal-angular';
import * as msal from '@azure/msal-browser';

import Utils from '../utils';

const GRAPH_API_BASE_URL = 'https://graph.microsoft.com';
const MICROSOFT_LOGIN_BASE_URL = 'https://login.microsoftonline.com';

class MicrosoftSignin {
  constructor(opts = {}) {
    this.opts = opts;

    this.adalBaseConfig = {
      clientId: opts.azureClientId,
      redirectUri: `${this.opts.appUrl}/integrations/microsoft/teams/silent`,
      cacheLocation: 'localStorage',
      navigateToLoginRequestUrl: false,
    };

    this.msalBaseConfig = {
      auth: {
        clientId: opts.azureClientId,
        redirectUri: `${this.opts.appUrl}/integrations/microsoft/teams/silent`,
      },
    };

    this.msalDefaultScopes = ['openid', 'email', 'profile'];

    microsoftTeams.initialize();
  }

  async msalRedirectCode() {
    // @inuse: sign in with microsoft-button on login page
    let msalInstance = new msal.PublicClientApplication(this.msalBaseConfig);
    await msalInstance.initialize();
    const { idToken: token, tenantId } = await msalInstance.handleRedirectPromise();
    return { token, tenantId };
  }

  async msalLogin(prompt = null, loginHint = null) {
    // @inuse: sign in with microsoft-button on login page
    let msalConfig = {
      ...this.msalBaseConfig,
    };
    msalConfig.auth.redirectUri = this.opts.appUrl;

    let msalInstance = new msal.PublicClientApplication(msalConfig);
    await msalInstance.initialize();

    let accountFilter = {};
    if (!prompt && loginHint) {
      accountFilter.loginHint = loginHint;
    }

    const response = await msalInstance.loginRedirect({
      account: msalInstance.getAccount(accountFilter),
      scopes: this.msalDefaultScopes,
      prompt,
    });
    msalInstance.setActiveAccount(response.account);
    const { tenantId, idToken: token } = response;

    return {
      tenantId,
      token,
    };
  }

  async msalActiveAccountAndPopup(prompt = null, forcePopup = false, setActiveAccount = true) {
    let msalInstance = new msal.PublicClientApplication(this.msalBaseConfig);
    await msalInstance.initialize();

    const popup = async () => {
      const response = await msalInstance.loginPopup({
        scopes: this.msalDefaultScopes,
        prompt,
      });
      const { idToken: token, tenantId, account } = response;
      if (setActiveAccount) {
        msalInstance.setActiveAccount(account);
      }
      return { token, tenantId };
    };

    const account = msalInstance.getActiveAccount();

    if (forcePopup) {
      return await popup();
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        account,
        scopes: this.msalDefaultScopes,
      });

      const { idToken: token, tenantId } = response;
      return { token, tenantId };
    } catch (error) {
      if (error.errorCode === 'no_account_error') {
        return await popup();
      }
    }
  }

  async msalTeamsSilentLogin(tenantId) {
    try {
      const response = await teamsAuthentication.getAuthToken({
        claims: this.msalDefaultScopes,
        silent: true,
      });
      return response;
    } catch (error) {
      return false;
    }
  }

  async msalSilentLogoutPopup() {
    // used by logout-button on profile settings page
    let msalInstance = new msal.PublicClientApplication(this.msalBaseConfig);
    await msalInstance.initialize();
    await msalInstance.logoutPopup({
      postLogoutRedirectUri: `${this.opts.appUrl}/logout`,
    });
  }

  async adalLogin(options = {}) {
    return new Promise((resolve, reject) => {
      try {
        const authContext = new AuthenticationContext({
          ...this.adalBaseConfig,
          popUp: true,
          callback: async (errorDesc, token, error, tokenType) => {
            try {
              if (token) {
                if (tokenType !== authContext.CONSTANTS.ID_TOKEN) {
                  token = authContext.getCachedToken(this.opts.azureClientId);
                }
                const user = authContext.getCachedUser();
                const tenantId = user && user.profile ? user.profile.tid : null;

                resolve({
                  tenantId,
                  token,
                  user,
                });
              } else {
                throw error;
              }
            } catch (error) {
              reject(error);
            }
          },
          ...options,
        });

        authContext.clearCache();
        authContext.login();
      } catch (error) {
        reject(error);
      }
    });
  }

  async adalTeamsLogin() {
    const response = await teamsAuthentication.authenticate({
      url: `${this.opts.appUrl}/integrations/microsoft/teams/initialize`,
      width: 600,
      height: 535,
    });
    const context = await microsoftTeams.getContext();

    const { idToken: token, accessToken } = response;
    const { tid: tenantId } = context;
    return {
      token,
      tenantId,
      accessToken,
    };
  }

  adalTeamsSilentLogin() {
    return new Promise((resolve, reject) => {
      microsoftTeams.getContext().then(async (context) => {
        const { upn: userPrincipalName, tid: tenantId } = context;

        let adalConfig = {
          ...this.adalBaseConfig,
          extraQueryParameter: 'scope=openid+email+profile',
        };

        if (userPrincipalName) {
          adalConfig.extraQueryParameter += '&login_hint=' + encodeURIComponent(userPrincipalName);
        }

        let authContext = new AuthenticationContext(adalConfig);
        let user = authContext.getCachedUser();
        if (user) {
          if (user.profile && user.profile.upn !== userPrincipalName) {
            // user doesnt match upn, clear cache
            authContext.clearCache();
          }
        }

        authContext.acquireToken(this.opts.azureClientId, async (errDesc, token, err, tokenType) => {
          if (token) {
            // make sure token is id token
            if (tokenType !== authContext.CONSTANTS.ID_TOKEN) {
              token = authContext.getCachedToken(this.opts.azureClientId);
            }

            resolve({ token, tenantId });
          } else {
            resolve(false);
          }
        });
      });
    });
  }

  // popup: callback for silent login
  async adalTeamsSilentCallback() {
    let authContext = new AuthenticationContext(this.adalBaseConfig);

    if (authContext.isCallback(window.location.hash)) {
      authContext.handleWindowCallback(window.location.hash);
      // popup? notify
      if (window.opener) {
        await microsoftTeams.initialize();
        if (authContext.getCachedUser()) {
          teamsAuthentication.notifySuccess();
        } else {
          teamsAuthentication.notifyFailure(authContext.getLoginError());
        }
      }
    }
  }

  // popup: initiate login
  async adalTeamsLoginInitialize(state, nonce, additional_scopes = '') {
    let queryParams = {
      client_id: this.opts.azureClientId,
      response_type: 'id_token token',
      response_mode: 'fragment',
      redirect_uri: `${this.opts.appUrl}/integrations/microsoft/teams/callback`,
      state,
      nonce,
      scope: `${GRAPH_API_BASE_URL}/User.Read openid email profile` + additional_scopes,
    };
    localStorage.setItem('state', state);

    await microsoftTeams.initialize();
    let url = `${MICROSOFT_LOGIN_BASE_URL}/common/oauth2/v2.0/authorize?${Utils.queryParamsFromObject(queryParams)}`;
    microsoftTeams.getContext().then((context) => {
      const urlWithLoginHint = `${url}&login_hint=${encodeURIComponent(context.user.userPrincipalName)}`;
      window.location.assign(urlWithLoginHint);
    });
  }

  // popup: callback for login
  async adalTeamsLoginCallback() {
    const params = Utils.hashParams(location.hash.substring(1));

    await microsoftTeams.initialize();
    if (params.error) {
      teamsAuthentication.notifyFailure(params.error);
    } else if (params.access_token) {
      if (params.state !== localStorage.getItem('state')) {
        teamsAuthentication.notifyFailure('StateDoesNotMatch');
      } else {
        teamsAuthentication.notifySuccess({
          idToken: params.id_token,
          accessToken: params.access_token,
          tokenType: params.token_type,
          expiresIn: params.expires_in,
        });
      }
    } else {
      teamsAuthentication.notifyFailure('UnexpectedFailure');
    }
  }

  adalClearCache() {
    let authContext = new AuthenticationContext(this.adalBaseConfig);
    authContext.clearCache();
  }

  // config tab
  teamsPostLogin(token, tenantId) {
    // TeamsConfigTab
    return new Promise((resolve, reject) => {
      microsoftTeams.getContext().then(async (context) => {
        try {
          const response = await fetch(`/integrations/microsoft/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              token,
              tenantId,
              subEntityId: context.page?.subPageId,
            }),
          });
          const data = await response.json();
          resolve(data);
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  // azure sso
  adminConsentCallback(params) {
    if (window.opener && window.opener.azureTenantAdminConsentComplete) {
      window.opener.azureTenantAdminConsentComplete(params);
      window.close();
    }
  }
}

export default MicrosoftSignin;
window['MicrosoftSignin'] = MicrosoftSignin;
